import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { LocationRow, PlantRow, PlantStatus } from "@garden/types/db";
import { supabase } from "../auth";
import { listPlants } from "../db/plants";
import { listLocations } from "../db/locations";
import { useCurrentGardenId } from "../lib/useCurrentGarden";
import "./PlantsList.css";

type SortKey = "common_name" | "planted_date";
type SortDir = "asc" | "desc";

export function PlantsList() {
  const [plants, setPlants] = useState<PlantRow[] | null>(null);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<PlantStatus | "all-active">("all-active");
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: "common_name",
    dir: "asc",
  });
  const navigate = useNavigate();
  const gardenId = useCurrentGardenId();

  useEffect(() => {
    if (!supabase || !gardenId) return;
    let active = true;
    Promise.all([
      listPlants(supabase, gardenId),
      listLocations(supabase, gardenId),
    ])
      .then(([p, l]) => {
        if (!active) return;
        setPlants(p);
        setLocations(l);
      })
      .catch((err) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : "Could not load plants.");
        setPlants([]);
      });
    return () => {
      active = false;
    };
  }, [gardenId]);

  const locById = useMemo(() => {
    const m = new Map<string, LocationRow>();
    for (const l of locations) m.set(l.id, l);
    return m;
  }, [locations]);

  const visible = useMemo(() => {
    if (!plants) return [];
    const filtered = plants.filter((p) => {
      if (statusFilter === "all-active") return p.status !== "removed";
      return p.status === statusFilter;
    });
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[sort.key] ?? "";
      const bv = b[sort.key] ?? "";
      if (av === bv) return 0;
      return av > bv ? dir : -dir;
    });
  }, [plants, statusFilter, sort]);

  const setSortKey = (key: SortKey) => {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
    );
  };

  if (plants === null) {
    return <div className="PlantsList__loading">Loading plants…</div>;
  }

  if (plants.length === 0) {
    return <PlantsEmptyState />;
  }

  return (
    <div className="PlantsList">
      <div className="PlantsList__header">
        <div>
          <div className="PlantsList__eyebrow">Catalogue</div>
          <h1 className="PlantsList__title">Plants</h1>
          <p className="PlantsList__count">
            {visible.length} {visible.length === 1 ? "plant" : "plants"}
            {statusFilter !== "all-active" && ` · ${statusFilter}`}
          </p>
        </div>
        <div className="PlantsList__toolbar">
          <select
            className="PlantsList__filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            aria-label="Filter by status"
          >
            <option value="all-active">Active (alive + needs id)</option>
            <option value="alive">Alive</option>
            <option value="needs_identification">Needs identification</option>
            <option value="dead">Dead</option>
            <option value="removed">Removed</option>
          </select>
          <button
            type="button"
            className="PlantsList__add"
            onClick={() => navigate("/plants/new")}
          >
            + Add plant
          </button>
        </div>
      </div>

      {error && <p className="PlantsList__error">{error}</p>}

      <div className="PlantsList__tablewrap">
        <table className="PlantsList__table">
          <thead>
            <tr>
              <th>
                <button type="button" onClick={() => setSortKey("common_name")}>
                  Name {sort.key === "common_name" ? (sort.dir === "asc" ? "↑" : "↓") : ""}
                </button>
              </th>
              <th className="PlantsList__col-sci">Scientific</th>
              <th>Location</th>
              <th>Status</th>
              <th className="PlantsList__col-date">
                <button type="button" onClick={() => setSortKey("planted_date")}>
                  Planted {sort.key === "planted_date" ? (sort.dir === "asc" ? "↑" : "↓") : ""}
                </button>
              </th>
              <th>Photos</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => (
              <tr
                key={p.id}
                className="PlantsList__row"
                onClick={() => navigate(`/plants/${p.id}`)}
              >
                <td className="PlantsList__name">
                  <Link to={`/plants/${p.id}`} onClick={(e) => e.stopPropagation()}>
                    {p.common_name}
                  </Link>
                  {p.cultivar && <span className="PlantsList__cultivar"> · '{p.cultivar}'</span>}
                </td>
                <td className="PlantsList__col-sci PlantsList__sci">
                  {p.scientific_name ?? <span className="PlantsList__faint">—</span>}
                </td>
                <td>
                  {p.location_id && locById.has(p.location_id) ? (
                    locById.get(p.location_id)!.name
                  ) : (
                    <span className="PlantsList__faint">—</span>
                  )}
                </td>
                <td>
                  <span className={`PlantsList__status PlantsList__status--${p.status}`}>
                    {p.status.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="PlantsList__col-date">
                  {p.planted_date ?? <span className="PlantsList__faint">—</span>}
                </td>
                <td className="PlantsList__faint">0</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PlantsEmptyState() {
  return (
    <div className="PlantsList__empty">
      <div className="PlantsList__empty-eyebrow">Welcome — let's get planted</div>
      <h1 className="PlantsList__empty-title">
        Your garden,<br />
        written <em>down</em>.
      </h1>
      <p className="PlantsList__empty-lede">
        A catalogue and a journal for one garden. Pick anywhere to begin.
      </p>

      <div className="PlantsList__cta-row">
        <Link to="/map" className="PlantsList__cta PlantsList__cta--soon">
          <span className="PlantsList__cta-num">01</span>
          <div className="PlantsList__cta-title">
            Set up<br />your garden map
          </div>
          <div className="PlantsList__cta-desc">Coming in stage 04.</div>
        </Link>

        <Link to="/plants/new" className="PlantsList__cta">
          <span className="PlantsList__cta-num">02</span>
          <div className="PlantsList__cta-title">
            Add your<br />first plant
          </div>
          <div className="PlantsList__cta-desc">
            Name it, drop a location, save. Notes can wait until later.
          </div>
        </Link>

        <a
          href="https://en.wikipedia.org/wiki/Special:Search"
          target="_blank"
          rel="noopener noreferrer"
          className="PlantsList__cta"
        >
          <span className="PlantsList__cta-num">03</span>
          <div className="PlantsList__cta-title">
            Browse<br />care info
          </div>
          <div className="PlantsList__cta-desc">
            A simple Wikipedia search. Paste what you want into a plant's notes.
          </div>
        </a>
      </div>
    </div>
  );
}
